import React, { useState } from 'react';
import { Percent } from 'lucide-react';
import { calculationService } from '../../../services/calculationService';

interface CategoryDiscountControlProps {
  categoryId: string;
  categoryTitle: string;
  subtotalBeforeDiscount: number;
  currentDiscount?: number;
  onApplyDiscount: (categoryId: string, percentage: number) => void;
  onRemoveDiscount: (categoryId: string) => void;
}

export const CategoryDiscountControl: React.FC<CategoryDiscountControlProps> = ({
  categoryId,
  categoryTitle,
  subtotalBeforeDiscount,
  currentDiscount,
  onApplyDiscount,
  onRemoveDiscount
}) => {
  const [percentage, setPercentage] = useState<string>(currentDiscount?.toString() || '');
  const [isEditing, setIsEditing] = useState(false);

  const parsedPercentage = parseFloat(percentage) || 0;
  const discountAmount = subtotalBeforeDiscount * (parsedPercentage / 100);
  const subtotalAfterDiscount = subtotalBeforeDiscount - discountAmount;

  const handleApply = () => {
    if (parsedPercentage > 0 && parsedPercentage <= 100) {
      onApplyDiscount(categoryId, parsedPercentage);
      setIsEditing(false);
    }
  };

  const handleRemove = () => {
    onRemoveDiscount(categoryId);
    setPercentage('');
    setIsEditing(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleApply();
    }
  };

  if (!isEditing && !currentDiscount) {
    return (
      <div style={{ marginTop: 12, paddingTop: 12, borderTop: '0.5px solid var(--color-border)' }}>
        <button
          onClick={() => setIsEditing(true)}
          onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, padding: '4px 10px', background: 'var(--gradient-primary)', border: 'none', borderRadius: 'var(--radius-sm)', color: 'white', cursor: 'pointer' }}
        >
          <Percent size={12} />
          Appliquer une décote
        </button>
      </div>
    );
  }

  return (
    <div className="mt-3 pt-3 border-t border-neutral-200 bg-neutral-50 p-3 rounded-md">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-neutral-700">Décote volume</span>
        {currentDiscount && !isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            Modifier
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={percentage}
              onChange={(e) => setPercentage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="% de décote"
              className="flex-1 px-3 py-2 text-sm border border-neutral-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <span className="text-sm text-neutral-600">%</span>
          </div>

          {parsedPercentage > 0 && parsedPercentage <= 100 && (
            <div className="space-y-1 text-xs text-neutral-600">
              <div className="flex justify-between">
                <span>Montant initial HT :</span>
                <span className="font-medium">{calculationService.formatPrice(subtotalBeforeDiscount)}</span>
              </div>
              <div className="flex justify-between text-red-600">
                <span>Décote ({parsedPercentage}%) :</span>
                <span className="font-medium">- {calculationService.formatPrice(discountAmount)}</span>
              </div>
              <div className="flex justify-between font-semibold text-neutral-800 pt-1 border-t border-neutral-300">
                <span>Nouveau montant HT :</span>
                <span>{calculationService.formatPrice(subtotalAfterDiscount)}</span>
              </div>
            </div>
          )}

          <div className="flex space-x-2">
            <button
              onClick={handleApply}
              disabled={parsedPercentage <= 0 || parsedPercentage > 100}
              className="flex-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Appliquer
            </button>
            {currentDiscount && (
              <button
                onClick={handleRemove}
                className="px-3 py-1.5 text-sm bg-neutral-200 text-neutral-700 rounded-md hover:bg-neutral-300"
              >
                Retirer
              </button>
            )}
            <button
              onClick={() => {
                setPercentage(currentDiscount?.toString() || '');
                setIsEditing(false);
              }}
              className="px-3 py-1.5 text-sm bg-neutral-200 text-neutral-700 rounded-md hover:bg-neutral-300"
            >
              Annuler
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-1 text-xs text-neutral-600">
          <div className="flex justify-between">
            <span>Montant initial HT :</span>
            <span className="font-medium">{calculationService.formatPrice(subtotalBeforeDiscount)}</span>
          </div>
          <div className="flex justify-between text-red-600">
            <span>Décote ({currentDiscount}%) :</span>
            <span className="font-medium">- {calculationService.formatPrice(discountAmount)}</span>
          </div>
          <div className="flex justify-between font-semibold text-neutral-800 pt-1 border-t border-neutral-300">
            <span>Nouveau montant HT :</span>
            <span>{calculationService.formatPrice(subtotalAfterDiscount)}</span>
          </div>
        </div>
      )}
    </div>
  );
};
